import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  Alert,
  Box,
  ButtonIcon,
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Link,
  Skeleton,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiInformationLine } from '@remixicon/react';
import { aiSpendMonitorApiRef, type UserSpend } from '../api';
import {
  AI_SPEND_BUDGET_REQUEST_THRESHOLD_PERCENTAGE,
  combineSpendFreshness,
  type SpendFreshness,
} from '@internal/plugin-ai-spend-monitor-common';
import { formatProviderName } from './providerName';
import { SpendValue } from './SpendValue';

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const usd = (amount: string | number): string =>
  usdFormatter.format(Number(amount));

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', {
  numeric: 'always',
});

/** `observedAt` as e.g. "3 hours ago"; null when it is not a valid date. */
const formatAge = (observedAt: string, now: number): string | null => {
  const elapsed = now - new Date(observedAt).getTime();
  if (Number.isNaN(elapsed)) return null;
  if (elapsed < MINUTE_MS) return 'just now';
  if (elapsed < HOUR_MS) {
    return relativeTimeFormatter.format(
      -Math.floor(elapsed / MINUTE_MS),
      'minute',
    );
  }
  if (elapsed < DAY_MS) {
    return relativeTimeFormatter.format(-Math.floor(elapsed / HOUR_MS), 'hour');
  }
  return relativeTimeFormatter.format(-Math.floor(elapsed / DAY_MS), 'day');
};

/** ISO date as day + short month in UTC, e.g. "24 Oct". */
const formatReachDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/** Spend-vs-budget view; `budget` is null when the caller is uncapped. */
const summarize = (data: UserSpend) => {
  const total = Number(data.totalUsd);
  const budget =
    data.limit.type === 'finite' ? Number(data.limit.amountUsd) : null;
  // Fraction of a *positive* budget used; null when unlimited or a $0 cap
  // (a $0 budget has no meaningful percentage — handled explicitly in render).
  const pct = budget !== null && budget > 0 ? total / budget : null;
  const over = budget !== null && total > budget;
  const threshold = AI_SPEND_BUDGET_REQUEST_THRESHOLD_PERCENTAGE / 100;
  // At/above the request threshold, including over budget and a $0 cap with
  // spend (which has no percentage but is over by definition).
  const nearLimit = over || (pct !== null && pct >= threshold);
  // Approaching the limit but not past it: amber rather than red.
  const warning = nearLimit && !over;
  return { total, budget, pct, over, nearLimit, warning };
};

/**
 * Provider amount as plain text with a short freshness note. The details sit
 * inside a tooltip, which must not contain interactive controls, so the
 * stale/unavailable markers of `SpendValue` are not nested here.
 */
const providerAmount = (
  amountUsd: string,
  freshness: SpendFreshness | undefined,
): string => {
  if (freshness?.status === 'unavailable') return 'Unavailable';
  return freshness?.status === 'stale'
    ? `${usd(amountUsd)} (stale)`
    : usd(amountUsd);
};

/**
 * Info icon at the end of the caption that reveals the secondary details
 * (per-provider spend, data age, lag note). Opens on hover, keyboard focus and
 * press, like the freshness markers in `SpendValue`.
 */
const SpendDetails = ({ children }: { children: NonNullable<ReactNode> }) => {
  const [open, setOpen] = useState(false);
  return (
    <TooltipTrigger
      isOpen={open}
      onOpenChange={setOpen}
      delay={0}
      closeDelay={0}
    >
      <ButtonIcon
        size="small"
        variant="tertiary"
        icon={<RiInformationLine size={16} />}
        aria-label="Spend details"
        onPress={() => setOpen(true)}
      />
      <Tooltip placement="bottom start">{children}</Tooltip>
    </TooltipTrigger>
  );
};

const AiSpendCardContent = () => {
  const api = useApi(aiSpendMonitorApiRef);
  const [data, setData] = useState<UserSpend | undefined>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getMySpend()
      .then(next => {
        if (active) setData(next);
      })
      .catch(next => {
        if (active) {
          setError(next instanceof Error ? next : new Error(String(next)));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  if (loading && !data) {
    return (
      <Box role="status" aria-label="Loading AI spend">
        <Skeleton width="100%" height={4} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert
        status="danger"
        role="alert"
        title="Unable to load AI spend"
        description={error.message}
      />
    );
  }
  if (!data) {
    // `undefined` maps to the backend's 404, which covers both "no snapshot
    // collected yet" and "identity/email could not be resolved". Keep the copy
    // neutral so an unresolved identity is not misread as genuine zero spend.
    return (
      <Text as="p" variant="body-small" color="secondary">
        AI spend data is not available yet.
      </Text>
    );
  }

  const { total, budget, pct, over, nearLimit, warning } = summarize(data);
  const budgetFreshness = combineSpendFreshness([
    data.freshness,
    data.limitFreshness,
  ]);
  const unavailable = budgetFreshness?.status === 'unavailable';

  // Caption cases: unlimited (no cap), a $0 cap (included-usage-only — no
  // meaningful %), or a positive budget shown as a percentage.
  let caption: string;
  if (budget === null) {
    caption = 'no budget set';
  } else if (budget === 0) {
    caption = over
      ? 'included usage only — over budget'
      : 'included usage only';
  } else {
    caption = `${Math.round((total / budget) * 100)}% of monthly budget${
      over ? ' — over budget' : ''
    }`;
  }

  // Bar fills to the used fraction of a positive budget; a $0 cap shows full
  // when there's any spend, empty otherwise.
  let barValue = 0;
  if (pct !== null) {
    barValue = Math.min(pct, 1) * 100;
  } else if (over) {
    barValue = 100;
  }

  // Run-out forecast; skipped once over budget, where it no longer applies.
  let forecastLine: string | undefined;
  if (data.forecast && !over) {
    forecastLine = data.forecast.limitReachedAt
      ? `At this pace you'll reach your budget around ${formatReachDate(
          data.forecast.limitReachedAt,
        )}`
      : "At this pace you'll stay within budget";
  }

  const age = formatAge(data.observedAt, Date.now());
  const hasDetails =
    data.spendByProvider.length > 0 || age !== null || nearLimit;
  const requestUrl = budget !== null ? data.budgetRequestUrl : undefined;
  let barColor = 'var(--bui-bg-solid)';
  if (over) barColor = 'var(--bui-fg-danger)';
  else if (warning) barColor = 'var(--bui-fg-warning)';
  return (
    <Flex direction="column" gap="2">
      <Text as="p" variant="title-small" weight="bold">
        <SpendValue value={usd(data.totalUsd)} freshness={data.freshness} />{' '}
        <Text as="span" variant="body-medium" color="secondary">
          <SpendValue
            value={budget === null ? '(no limit)' : `of ${usd(budget)}`}
            freshness={data.limitFreshness}
          />
        </Text>
      </Text>
      {!unavailable && (
        // BUI has no progress bar, and Chrome ignores the height of a native
        // <progress> with its default appearance, so the bar is composed from
        // two Boxes on BUI tokens and keeps the progressbar semantics.
        <Box
          role="progressbar"
          aria-label="Monthly AI spend against budget"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(barValue)}
          style={{
            height: 'calc(var(--bui-space) * 2.5)',
            borderRadius: 'var(--bui-radius-full)',
            backgroundColor: 'var(--bui-bg-neutral-2)',
            overflow: 'hidden',
          }}
        >
          <Box
            style={{
              width: `${barValue}%`,
              height: '100%',
              backgroundColor: barColor,
            }}
          >
            {null}
          </Box>
        </Box>
      )}
      <Text
        as="p"
        variant="body-small"
        color={warning ? 'warning' : 'secondary'}
      >
        <SpendValue value={caption} freshness={budgetFreshness} />
        {hasDetails && (
          <SpendDetails>
            {data.spendByProvider.map(provider => (
              <Box key={provider.provider}>
                {formatProviderName(provider.provider)}{' '}
                {providerAmount(provider.amountUsd, provider.freshness)}
              </Box>
            ))}
            {age && <Box>Updated {age}</Box>}
            {nearLimit && (
              <Box>
                Spend data can lag behind, so if you're using AI heavily, ask
                for more now.
              </Box>
            )}
          </SpendDetails>
        )}
      </Text>
      {forecastLine && (
        <Text as="p" variant="body-small" color="secondary">
          {forecastLine}
        </Text>
      )}
      {requestUrl &&
        (nearLimit ? (
          <Box pt="2">
            <ButtonLink
              variant="primary"
              size="small"
              href={requestUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Request more budget
            </ButtonLink>
          </Box>
        ) : (
          <Text as="p" variant="body-x-small">
            <Link href={requestUrl} target="_blank" rel="noopener noreferrer">
              Need more? Request an increase
            </Link>
          </Text>
        ))}
    </Flex>
  );
};

/**
 * Backstage home-page card: shows the signed-in engineer their month-to-date
 * AI spend against their effective monthly budget, sourced from the
 * ai-spend-monitor backend's `/me` endpoint (spend + Anthropic effective
 * limit). This is the per-engineer self-service surface the reporting page
 * (admin/audit) does not provide.
 */
export const HomePageAiSpendCard = () => (
  <Card aria-labelledby="home-ai-spend-title">
    <CardHeader>
      <Text as="h2" variant="title-small" id="home-ai-spend-title">
        Your AI spend
      </Text>
    </CardHeader>
    <CardBody>
      <AiSpendCardContent />
    </CardBody>
  </Card>
);
